import * as React from 'react';

import { Tooltip } from '@dndbeyond/character-common-components/es';
import {
    Ability,
    AbilityUtils,
    CharacterTheme,
    FormatUtils,
    SituationalSavingThrowInfo,
    SituationalSavingThrowInfoLookup,
} from '@dndbeyond/character-rules-engine/es';
import { DiceTools, IRollContext, RollType } from '@dndbeyond/dice';
import { GameLogContext } from '@dndbeyond/game-log-components';

import BoxBackground from '../BoxBackground';
import { SignedNumberDice } from '../Dice';
import { ProficiencyLevelIcon } from '../Icons';
import {
    ThemedSavingThrowSelectionBoxSvg,
    ThemedSavingThrowSelectionSmallBoxSvg,
    ThemedSavingThrowRowBoxSvg,
    ThemedSavingThrowRowSmallBoxSvg,
    NegativeBonusNegativeSvg,
    PositiveBonusPositiveSvg,
    DarkModePositiveBonusPositiveSvg,
    DarkModeNegativeBonusNegativeSvg,
} from '../Svg';

interface Props {
    abilities: Array<Ability>;
    situationalBonusSavingThrowsLookup: SituationalSavingThrowInfoLookup;
    onClick?: (ability: Ability) => void;
    rowStyle: 'small' | 'normal';
    className: string;
    diceEnabled: boolean;
    theme: CharacterTheme;
    rollContext: IRollContext;
}
class SavingThrowsSummary extends React.PureComponent<Props> {
    static defaultProps = {
        className: '',
        diceEnabled: false,
        rowStyle: 'normal',
    };

    handleClick = (ability: Ability, evt: React.MouseEvent): void => {
        const { onClick } = this.props;

        if (onClick) {
            evt.stopPropagation();
            evt.nativeEvent.stopImmediatePropagation();
            onClick(ability);
        }
    };

    render() {
        const { abilities, situationalBonusSavingThrowsLookup, diceEnabled, rowStyle, theme, rollContext } = this.props;

        const [{ messageTargetOptions, defaultMessageTargetOption, userId }] = this.context;

        return (
            <div className="ddbc-saving-throws-summary">
                {abilities.map((ability) => {
                    let situationalBonusSavingThrows: Array<SituationalSavingThrowInfo> = [];
                    let maxOptBonus: number = 0;

                    if (situationalBonusSavingThrowsLookup) {
                        situationalBonusSavingThrows = situationalBonusSavingThrowsLookup[AbilityUtils.getId(ability)];
                        if (situationalBonusSavingThrows && situationalBonusSavingThrows.length) {
                            let situationalBonusSavingThrowsValues: Array<number> = situationalBonusSavingThrows.map(
                                (optBonus) => optBonus.value,
                            );
                            maxOptBonus = Math.max(...situationalBonusSavingThrowsValues);
                        }
                    }

                    let situationalBonusClasses: Array<string> = ['ddbc-saving-throws-summary__ability-situational'];
                    let IconComponent: React.ComponentType<any> | null = null;
                    if (maxOptBonus < 0) {
                        situationalBonusClasses.push('ddbc-saving-throws-summary__ability-situational--bonus-neg');
                        IconComponent = theme?.isDarkMode ? DarkModeNegativeBonusNegativeSvg : NegativeBonusNegativeSvg;
                    } else {
                        situationalBonusClasses.push('ddbc-saving-throws-summary__ability-situational--bonus-pos');
                        IconComponent = theme?.isDarkMode ? DarkModePositiveBonusPositiveSvg : PositiveBonusPositiveSvg;
                    }

                    let StyleComponent: React.ComponentType<any> = ThemedSavingThrowRowBoxSvg;
                    let SelectionBox = ThemedSavingThrowSelectionBoxSvg;
                    if (rowStyle === 'small') {
                        StyleComponent = ThemedSavingThrowRowSmallBoxSvg;
                        SelectionBox = ThemedSavingThrowSelectionSmallBoxSvg;
                    }

                    return (
                        <div
                            key={AbilityUtils.getStatKey(ability)}
                            className={`ddbc-saving-throws-summary__ability ddbc-saving-throws-summary__ability--${FormatUtils.slugify(
                                AbilityUtils.getStatKey(ability),
                            )}`}
                            onClick={this.handleClick.bind(this, ability)}
                        >
                            <BoxBackground
                                StyleComponent={StyleComponent}
                                theme={{
                                    ...theme,
                                    themeColor: `${theme.themeColor}80`,
                                }}
                            />
                            <div className="ddbc-saving-throws-summary__ability-proficiency">
                                <ProficiencyLevelIcon
                                    proficiencyLevel={AbilityUtils.getProficiencyLevel(ability)}
                                    isModified={AbilityUtils.getIsSaveProficiencyModified(ability)}
                                    theme={theme}
                                />
                            </div>
                            <div
                                className={`ddbc-saving-throws-summary__ability-name ${
                                    theme.isDarkMode ? 'ddbc-saving-throws-summary__ability-name--dark-mode' : ''
                                }`}
                            >
                                {AbilityUtils.getName(ability)}
                            </div>
                            <div className="ddbc-saving-throws-summary__ability-modifier">
                                <SignedNumberDice
                                    number={AbilityUtils.getSave(ability)}
                                    isModified={AbilityUtils.getIsSaveModifierModified(ability)}
                                    diceNotation={DiceTools.CustomD20(AbilityUtils.getSave(ability))}
                                    isDarkMode={theme.isDarkMode}
                                    rollType={RollType.Save}
                                    rollAction={AbilityUtils.getName(ability)}
                                    diceEnabled={diceEnabled}
                                    advMenu={true}
                                    themeColor={theme.themeColor}
                                    rollContext={rollContext}
                                    rollTargetOptions={
                                        messageTargetOptions ? Object.values(messageTargetOptions?.entities) : undefined
                                    }
                                    rollTargetDefault={defaultMessageTargetOption}
                                    userId={userId}
                                />
                                <SelectionBox
                                    theme={theme}
                                    className={`ddbc-saving-throws-summary__ability-modifier-background`}
                                />
                            </div>
                            {situationalBonusSavingThrows && situationalBonusSavingThrows.length > 0 && (
                                <Tooltip
                                    className={situationalBonusClasses.join(' ')}
                                    isDarkMode={theme.isDarkMode}
                                    title={`Situational Bonus${situationalBonusSavingThrows.length !== 1 ? 'es' : ''}`}
                                >
                                    <IconComponent className="ddbc-saving-throws-summary__ability-situational-icon" />
                                </Tooltip>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
}

SavingThrowsSummary.contextType = GameLogContext;

export default SavingThrowsSummary;
